import { getBoundedValue } from 'utils/math/math.utils';
import {
  getForecastByTimeOfDay,
  getForecastForDay,
} from '../../../features/DetailedForecast/utils/detailedForecast.utils';

export const getDetailedForecastWidgetLayout = ({ containerSizePx, layoutSize, dimensions }) => {
  const columnCount = Math.max(1, Number(dimensions?.[0]) || 1);
  const isSingleColumnWidget = columnCount === 1;
  const containerWidth = Number.isFinite(containerSizePx?.width)
    ? containerSizePx.width
    : layoutSize.widgetWidth * columnCount;
  const baseFontSize = Number.isFinite(layoutSize?.baseFontSize) ? layoutSize.baseFontSize : 12;

  const arrowButtonWidth = getBoundedValue(
    Math.round(containerWidth * (isSingleColumnWidget ? 0.11 : 0.065)),
    30,
    50,
  );
  const compact1x1VisibleCount = containerWidth <= 340 ? 2 : 3;
  const compact1x2ColumnCount = containerWidth < 360 ? 2 : containerWidth < 480 ? 3 : 4;
  const tileFontSize = getBoundedValue(
    Math.min(baseFontSize, Math.round(containerWidth * 0.04)),
    11,
    14,
  );

  return {
    arrowButtonWidth,
    arrowIconSize: getBoundedValue(Math.round(arrowButtonWidth * 0.42), 14, 20),
    compact1x1VisibleCount,
    compact1x2ColumnCount,
    rowTileIconSize: getBoundedValue(Math.round(containerWidth * 0.09), 26, 38),
    tileIconSize: getBoundedValue(Math.round(containerWidth * 0.11), 30, 46),
    tileTextStyle: {
      fontSize: tileFontSize,
      lineHeight: 1.05,
    },
  };
};

/**
 * Returns the unique weekdays present in the forecast data, sorted for a
 * forward-looking forecast starting from today.
 *
 * Weekdays earlier than today are treated as occurring next week so the result
 * stays chronological. For example, if today is Thursday, Sunday is sorted after
 * Friday and Saturday instead of before them.
 *
 * Days not present in the forecast data are omitted.
 *
 * @param {Array} forecast - Forecast items containing `weekday` and `day_of_week` values.
 * @returns {string[]} Unique weekday names available in the forecast data, ordered by upcoming occurrence.
 */
export const getDetailedForecastDays = (forecast = []) => {
  const today = new Date().getDay();
  const daysByNameMap = new Map();

  forecast.forEach((forecastItem) => {
    if (!forecastItem?.weekday || daysByNameMap.has(forecastItem.weekday)) return;

    const dayOfWeek = parseInt(forecastItem.day_of_week, 10);
    daysByNameMap.set(forecastItem.weekday, {
      weekday: forecastItem.weekday,
      order: dayOfWeek < today ? dayOfWeek + 7 : dayOfWeek, // Adds 7 because any day before today should be considered as part of the next week
    });
  });

  // Asc order by order (day of week) to ensure the days are in the correct order for display
  return Array.from(daysByNameMap.values())
    .sort((a, b) => a.order - b.order)
    .map((day) => day.weekday);
};

export const getDetailedForecastTimeOfDayItems = (
  forecast = [],
  selectedDay,
  timeOfDayOrder = [],
) => {
  const dayForecast = getForecastForDay(forecast, selectedDay);
  const forecastByTimeOfDay = getForecastByTimeOfDay(dayForecast);
  return timeOfDayOrder.map((label) => forecastByTimeOfDay[label.toLowerCase()]).filter(Boolean);
};

export const getDetailedForecastTileAriaLabel = (item) => {
  const segmentLabel = item?.day_segment || 'this time';
  const temperatureLabel =
    item?.temperature === null || item?.temperature === undefined
      ? 'temperature unavailable'
      : `${item.temperature} degrees Fahrenheit`;

  return `Select detailed forecast for ${segmentLabel}, ${temperatureLabel}`;
};
